import { Controller } from "@hotwired/stimulus"

// Phase 2.7 Tier 2 — Linked Items panel on meeting item cards.
// Mirrors `risk_link_controller.js` ergonomically: type-aware
// picker (Punchlist / Risk / PO / etc.), debounced search, JSON
// CRUD against `MeetingItemLinksController`. DOM updates so the
// list refreshes inline without reloading the meeting page.
export default class extends Controller {
  static targets = [
    "list", "trigger", "triggerLabel", "row", "picker",
    "typeSelect", "kindSelect", "search", "results",
    "confirmModal", "confirmLabel"
  ]
  static values = {
    createUrl: String,
    pickerUrl: String,
    allowedTypes: Array
  }

  connect() {
    this._searchTimer = null
    this._pendingRemoval = null
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  openPicker() {
    if (!this.hasPickerTarget) return
    this.pickerTarget.classList.remove("hidden")
    if (this.hasSearchTarget) {
      this.searchTarget.value = ""
      this.searchTarget.focus()
    }
    this._loadCandidates()
  }

  closePicker() {
    if (!this.hasPickerTarget) return
    this.pickerTarget.classList.add("hidden")
  }

  typeChanged() {
    this._loadCandidates()
  }

  searchChanged() {
    if (this._searchTimer) clearTimeout(this._searchTimer)
    this._searchTimer = setTimeout(() => this._loadCandidates(), 220)
  }

  async _loadCandidates() {
    if (!this.hasResultsTarget) return
    const type = this.typeSelectTarget.value
    const q = this.hasSearchTarget ? this.searchTarget.value : ""

    this.resultsTarget.innerHTML = `<li class="px-2 py-2 text-[12px] text-gray-400">Searching…</li>`

    try {
      const url = `${this.pickerUrlValue}?linkable_type=${encodeURIComponent(type)}&q=${encodeURIComponent(q)}`
      const resp = await fetch(url, {
        headers: { "Accept": "application/json", "X-CSRF-Token": this._csrf() },
        credentials: "same-origin"
      })
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
      const data = await resp.json()
      this._renderCandidates(data.rows || [], type)
    } catch (e) {
      console.error("[MeetingLink] picker error", e)
      this.resultsTarget.innerHTML = `<li class="px-2 py-2 text-[12px] text-rose-700">Failed to load — try again.</li>`
    }
  }

  _renderCandidates(rows, type) {
    if (rows.length === 0) {
      this.resultsTarget.innerHTML = `<li class="px-2 py-2 text-[12px] text-gray-400">No matching ${type} found.</li>`
      return
    }
    this.resultsTarget.innerHTML = rows.map(row => {
      const thumb = row.thumb
        ? `<img src="${this._esc(row.thumb)}" alt="" loading="lazy" class="w-8 h-8 rounded object-cover bg-gray-100 shrink-0">`
        : ""
      return `
      <li class="px-2 py-1.5 hover:bg-brand/5 flex items-center gap-2 cursor-pointer"
          data-action="click->meeting-link#pickCandidate"
          data-candidate-id="${row.id}"
          data-candidate-label="${this._esc(row.label)}">
        ${thumb}
        <span class="text-[12px] text-gray-700 flex-1 truncate">${this._esc(row.label)}</span>
        <span class="text-[10px] text-brand font-medium">+ Link</span>
      </li>`
    }).join("")
  }

  async pickCandidate(event) {
    const linkableId = event.currentTarget.dataset.candidateId
    const linkableType = this.typeSelectTarget.value
    // The link-kind picker was removed; default to "references". Fall
    // back gracefully if the select is ever reintroduced.
    const linkKind = this.hasKindSelectTarget ? this.kindSelectTarget.value : "references"
    if (!linkableId) return

    try {
      const resp = await fetch(this.createUrlValue, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": this._csrf()
        },
        credentials: "same-origin",
        body: JSON.stringify({
          linkable_type: linkableType,
          linkable_id: linkableId,
          link_kind: linkKind
        })
      })
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}))
        this._showToast({ message: (err.errors || ["Could not link"]).join(" — "), type: "error" })
        return
      }
      const data = await resp.json()
      this._appendRow(data.row)
      this.closePicker()
    } catch (e) {
      console.error("[MeetingLink] create error", e)
      this._showToast({ message: "Failed to link — please retry.", type: "error" })
    }
  }

  remove(event) {
    const row = event.currentTarget.closest("[data-link-id]")
    if (!row) return
    this._pendingRemoval = { row, url: row.dataset.destroyUrl }
    if (this.hasConfirmLabelTarget) {
      const label = row.querySelector("a, .text-gray-800")?.textContent?.trim() || "this item"
      this.confirmLabelTarget.textContent = label
    }
    if (this.hasConfirmModalTarget) {
      this.confirmModalTarget.classList.remove("hidden")
    }
  }

  cancelRemove() {
    this._pendingRemoval = null
    if (this.hasConfirmModalTarget) {
      this.confirmModalTarget.classList.add("hidden")
    }
  }

  async confirmRemove() {
    const pending = this._pendingRemoval
    if (!pending) return
    this.cancelRemove()
    try {
      const resp = await fetch(pending.url, {
        method: "DELETE",
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": this._csrf()
        },
        credentials: "same-origin"
      })
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
      pending.row.remove()
      this._refreshEmptyState()
    } catch (e) {
      console.error("[MeetingLink] destroy error", e)
      this._showToast({ message: "Failed to unlink — please retry.", type: "error" })
    }
  }

  _appendRow(row) {
    if (!row) return
    const url = row.url
    const summary = this._esc(row.summary || "")
    const kindRaw = (row.link_kind || "").replace(/_/g, " ")
    const kind = this._esc(kindRaw ? kindRaw.charAt(0).toUpperCase() + kindRaw.slice(1) : "")
    const chip = `
      <div class="flex w-full items-start gap-2 px-2.5 py-1.5 rounded-lg text-[13px] bg-gray-50 border border-gray-200"
            data-link-id="${row.id}"
            data-meeting-link-target="row"
            data-destroy-url="${this.createUrlValue}/${row.id}"
            title="${kind}">
        <svg class="w-4 h-4 text-brand shrink-0 mt-0.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M13.828 10.172a4 4 0 015.656 0l1.414 1.414a4 4 0 010 5.656l-2.828 2.828a4 4 0 01-5.656 0M10.172 13.828a4 4 0 01-5.656 0L3.1 12.414a4 4 0 010-5.656L5.929 3.93a4 4 0 015.656 0"/></svg>
        <div class="min-w-0 flex-1 break-words">
          ${kind ? `<span class="text-gray-400">${kind}</span> <span class="text-gray-300">·</span> ` : ""}${url ? `<a href="${url}" target="_blank" class="text-gray-800 hover:underline">${summary}</a>` : `<span class="text-gray-800">${summary}</span>`}
        </div>
        <button type="button" data-action="click->meeting-link#remove"
                class="shrink-0 text-gray-400 hover:text-gray-600 cursor-pointer leading-none text-sm">×</button>
      </div>`
    // Insert before the trigger button so rows stay above it.
    if (this.hasTriggerTarget) {
      this.triggerTarget.insertAdjacentHTML("beforebegin", chip)
    } else if (this.hasListTarget) {
      this.listTarget.insertAdjacentHTML("afterbegin", chip)
    }
    if (this.hasTriggerLabelTarget) this.triggerLabelTarget.textContent = "Link"
  }

  _refreshEmptyState() {
    if (!this.hasListTarget) return
    const hasChips = this.listTarget.querySelectorAll("[data-link-id]").length > 0
    if (!hasChips && this.hasTriggerLabelTarget) {
      this.triggerLabelTarget.textContent = "Link to an item"
    }
  }

  _csrf() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  _esc(str) {
    if (str == null) return ""
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;")
  }

  _showToast({ message, type = "success" }) {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(msgP)
    document.body.prepend(el)
  }
}
